import { useState, useEffect } from 'react';
import { AlertTriangle, User, MapPin, Route, Loader2, XCircle, CheckCircle } from 'lucide-react';
import unifiedApi from '../services/unifiedApi';
import logger from '@/utils/logger';

/**
 * Modale de confirmation avec aperçu des impacts en cascade
 * TICKET-06: Affiche les modifications qui seront effectuées avant suppression/changement de statut
 */
const StudentImpactConfirmationModal = ({
  student,
  action, // 'delete' | 'activate' | 'deactivate'
  onConfirm,
  onCancel,
}) => {
  const [impacts, setImpacts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadImpacts();
  }, [student, action]);

  const loadImpacts = async () => {
    if (!student) return;

    setLoading(true);
    setError(null);

    try {
      let response;

      if (action === 'delete') {
        response = await unifiedApi.students.previewDelete(student.id);
      } else {
        const newStatus = action === 'activate' ? 'active' : 'inactive';
        response = await unifiedApi.students.previewStatusChange(student.id, newStatus);
      }

      if (response.success) {
        setImpacts(response.impacts);
      } else {
        throw new Error(response.error || 'Erreur lors du chargement des impacts');
      }
    } catch (err) {
      logger.error('Erreur chargement impacts:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (action) {
      case 'delete':
        return 'Confirmation de suppression';
      case 'activate':
        return "Confirmation d'activation";
      case 'deactivate':
        return 'Confirmation de désactivation';
      default:
        return 'Confirmation';
    }
  };

  const getDescription = () => {
    const studentName = `${student?.firstName || student?.first_name} ${student?.lastName || student?.last_name}`;

    switch (action) {
      case 'delete':
        return `Vous êtes sur le point de supprimer définitivement l'élève ${studentName}.`;
      case 'activate':
        return `Vous êtes sur le point d'activer l'élève ${studentName}.`;
      case 'deactivate':
        return `Vous êtes sur le point de désactiver l'élève ${studentName}.`;
      default:
        return '';
    }
  };

  const getColorClasses = () => {
    switch (action) {
      case 'delete':
        return {
          border: 'border-red-500',
          iconBg: 'bg-red-100',
          iconColor: 'text-red-600',
          sectionBg: 'bg-red-50',
          textColor: 'text-red-700',
          textBold: 'text-red-800',
          button: 'bg-red-600 hover:bg-red-700',
        };
      case 'activate':
        return {
          border: 'border-green-500',
          iconBg: 'bg-green-100',
          iconColor: 'text-green-600',
          sectionBg: 'bg-green-50',
          textColor: 'text-green-700',
          textBold: 'text-green-800',
          button: 'bg-green-600 hover:bg-green-700',
        };
      case 'deactivate':
        return {
          border: 'border-orange-500',
          iconBg: 'bg-orange-100',
          iconColor: 'text-orange-600',
          sectionBg: 'bg-orange-50',
          textColor: 'text-orange-700',
          textBold: 'text-orange-800',
          button: 'bg-orange-600 hover:bg-orange-700',
        };
      default:
        return {};
    }
  };

  const colors = getColorClasses();

  const renderImpacts = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          <span className="ml-2 text-sm text-gray-600">Analyse des impacts en cours...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center py-8 text-red-600">
          <XCircle className="w-5 h-5 mr-2" />
          <span className="text-sm">Erreur: {error}</span>
        </div>
      );
    }

    if (!impacts) return null;

    return (
      <div className={`${colors.sectionBg} rounded-lg p-4 mb-6 space-y-3`}>
        <p className={`text-sm font-medium ${colors.textBold} mb-2`}>
          {action === 'delete' ? 'Conséquences de la suppression :' : 'Modifications qui seront effectuées :'}
        </p>

        {/* Élève */}
        <div className={`flex items-start text-sm ${colors.textColor}`}>
          <User className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
          <span>
            <strong>Élève :</strong> {impacts.student?.name || 'Inconnu'}
            {action === 'delete' ? ' sera supprimé(e)' : ` → ${impacts.newStatus === 'active' ? 'Actif' : 'Inactif'}`}
          </span>
        </div>

        {/* Parent */}
        {impacts.parent && (
          <div className={`flex items-start text-sm ${colors.textColor}`}>
            <User className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
            <span>
              <strong>Parent :</strong> {impacts.parent.name}
              {action === 'delete' && impacts.willDeleteParent && ' sera supprimé(e) (dernier enfant)'}
              {action === 'delete' && impacts.willDeactivateParent && ' sera désactivé(e) (dernier enfant actif)'}
              {action === 'deactivate' && impacts.willChangeParentStatus && ' sera désactivé(e) (dernier enfant actif)'}
              {action === 'activate' && impacts.willChangeParentStatus && ' sera activé(e)'}
              {!impacts.willDeleteParent &&
                !impacts.willDeactivateParent &&
                !impacts.willChangeParentStatus &&
                ' (aucun changement)'}
            </span>
          </div>
        )}

        {/* Arrêt */}
        {impacts.stop && (
          <div className={`flex items-start text-sm ${colors.textColor}`}>
            <MapPin className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
            <span>
              <strong>Arrêt :</strong> {impacts.stop.name}
              {action === 'delete' && impacts.willDeleteStop && ' sera supprimé (dernier élève)'}
              {action === 'delete' && impacts.willDeactivateStop && ' sera désactivé (dernier élève actif)'}
              {action === 'deactivate' && impacts.willChangeStopStatus && ' sera désactivé (dernier élève actif)'}
              {action === 'activate' && impacts.willChangeStopStatus && ' sera activé'}
              {!impacts.willDeleteStop &&
                !impacts.willDeactivateStop &&
                !impacts.willChangeStopStatus &&
                ' (aucun changement)'}
            </span>
          </div>
        )}

        {/* Circuits affectés */}
        {impacts.affectedRoutesCount > 0 && (
          <div className={`flex items-start text-sm ${colors.textColor}`}>
            <Route className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
            <span>
              <strong>Circuits :</strong> {impacts.affectedRoutesCount} circuit(s) seront mis à jour
              {impacts.routes && impacts.routes.length > 0 && (
                <span className="block mt-1 ml-6 text-xs">
                  {impacts.routes.map((r) => r.name || r.code).join(', ')}
                </span>
              )}
            </span>
          </div>
        )}

        {/* Aucun impact */}
        {!impacts.parent && !impacts.stop && impacts.affectedRoutesCount === 0 && (
          <div className="flex items-center text-sm text-gray-600">
            <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
            <span>Aucun impact sur d'autres entités</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div
        className={`bg-white rounded-xl shadow-2xl max-w-md w-full p-6 border-l-4 ${colors.border} animate-in fade-in zoom-in duration-200`}
      >
        <div className="flex items-start mb-4">
          <div className={`p-2 ${colors.iconBg} rounded-full mr-3`}>
            <AlertTriangle className={`w-6 h-6 ${colors.iconColor}`} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">{getTitle()}</h3>
            <p className="text-sm text-gray-600 mt-1">{getDescription()}</p>
          </div>
        </div>

        {renderImpacts()}

        <div className="flex space-x-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            disabled={loading}
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 px-4 py-2 ${colors.button} text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed`}
            disabled={loading || error}
          >
            {loading ? 'Chargement...' : 'Confirmer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentImpactConfirmationModal;
